import styled, { css } from 'vue-styled-components';
import { map, tap, filter, switchMap, auditTime } from 'rxjs/operators';
import { KEY_CODE } from '@consts';
import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import Tags from '@/components/Common/Tags.vue';
import { applyHoverAndPress } from '@styles/common.styled';
import debounce from 'lodash/debounce';

const INPUT_PROPS = {
    hasError: Boolean,
    focus: Boolean,
    tagColor: String,
    fontColor: String,
};
const FONT_FAMILY = css`
    font-family: 'Noto Sans', 'Font Awesome 5 Pro', sans-serif;
`;

const { backspace } = KEY_CODE;

export default {
    name: 'AutoCompleteTags',
    created() {
        this.$nextTick(() => {
            const input$ = this.$watchAsObservable('text', {
                immediate: true,
            }).pipe(
                tap(() => (this.results = [])),
                auditTime(250),
                map(() => (this.text ? this.text.trim() : '')),
                filter(R.propSatisfies(R.gt(R.__, 0), 'length')),
                tap(() => (this.isLoading = true)),
                switchMap(this.api),
                map((value) => R.without(this.values, value)),
                tap(() => (this.isLoading = false))
            );

            this.$subscribeTo(input$, (value = []) => {
                this.results = value;
            });
        });
    },
    props: {
        dirty: {
            type: Boolean,
            default: false,
        },
        values: {
            type: [Array, Object],
        },
        api: {
            type: Function,
            default: R.always(Promise.resolve([])),
        },
        handleAdd: {
            type: Function,
            default: R.identity,
        },
        handleRemove: {
            type: Function,
            default: R.identity,
        },
        formatter: {
            type: Function,
            default: R.prop('text'),
        },
        tagColor: {
            type: String,
            default: '#f3b94d',
        },
        placeholder: {
            type: String,
        },
        tagValueGetter: {
            type: Function,
            required: true,
        },
        removable: {
            type: Boolean,
            default: true,
        },
        fontColor: {
            type: String,
            default: 'white',
        },
    },
    data() {
        return {
            text: '',
            isLoading: false,
            focusInput: true,
            results: [],
            readyToDelete: false,
        };
    },
    methods: {
        forceFocus() {
            const input = this.$refs.input;
            input && input.focus();
        },
        renderTags() {
            const toChild = {
                attrs: {
                    className: 'auto-complete-tags',
                    'max-width': '100%',
                    ...R.pick(['values', 'removable'], this),
                    'value-getter': this.tagValueGetter,
                },
                on: {
                    'update:remove': (index) =>
                        this.handleRemove({
                            index,
                            type: R.path(['values', index, 'type'], this),
                        }),
                },
            };

            return <Tags {...toChild}>{this.renderInput()}</Tags>;
        },
        handleKeydown: debounce(
            function({ keyCode }) {
                if (keyCode === backspace && !this.text) {
                    if (this.readyToDelete) {
                        this.handleRemove({
                            index: R.dec(this.values.length),
                            type: 'search',
                        });
                        this.readyToDelete = false;
                    } else {
                        this.readyToDelete = true;
                    }
                } else {
                    this.readyToDelete = false;
                }
            },
            100,
            { trailing: false, leading: true }
        ),
        renderInput() {
            let lastChild = '';
            let hasError = false;
            if (this.dirty === true) {
                hasError = true;
                const directives = [
                    {
                        name: 'tooltip',
                        value: {
                            content: this.$t('COMMON.required'),
                            position: 'bottom-end',
                        },
                    },
                ];
                lastChild = (
                    <button {...{ directives }}>
                        <i class="fas fa-map-marker-exclamation" />
                    </button>
                );
            } else if (this.isLoading) {
                lastChild = <ClipLoader color="#999" size="20px" />;
            }

            return (
                <InputWrapper slot="input" hasError={hasError}>
                    <b-autocomplete
                        value={this.text}
                        onInput={(v) => (this.text = v)}
                        data={this.results}
                        placeholder={this.placeholder}
                        custom-formatter={this.formatter}
                        clear-on-select={true}
                        onSelect={(v) => {
                            if (!v) {
                                return;
                            }

                            this.handleAdd(v, this.results);
                            this.readyToDelete = false;
                        }}
                        ref="input"
                        onfocus={() => (this.focusInput = true)}
                        onBlur={() => {
                            this.focusInput = false;
                            this.readyToDelete = false;
                        }}
                        nativeOnKeydown={this.handleKeydown}
                    />
                    {lastChild}
                </InputWrapper>
            );
        },
    },
    render() {
        return (
            <Wrapper
                focus={this.focusInput}
                onClick={this.forceFocus}
                tagColor={this.tagColor}
                fontColor={this.fontColor}
                class={{ 'remove-disabled': !this.removable }}
            >
                {this.renderTags()}
            </Wrapper>
        );
    },
};

const Wrapper = styled('div', INPUT_PROPS)`
    width: 100%;
    border: solid 1px #cccccc;
    ${(props) =>
        props.focus &&
        css`
            border: solid 1px #0054c6;
            box-shadow: 0 0 10px 0 rgba(90, 171, 227, 0.5);
        `}
    display: flex;
    flex-wrap: wrap;
    .tags-container.auto-complete-tags {
        margin: 0;
        padding: 0 5px;
        .tags-element {
            margin: 5px 3.5px;
            color: ${R.prop('fontColor')};
            height: 25px;
            padding: 0 10px;
            border-radius: 45px;
            max-width: 100%;
            background-color: ${R.prop('tagColor')};
            top: 2px;
            position: relative;
            .tags-text {
                ${FONT_FAMILY}
                font-size: 12px;
                margin: 5px 3.5px;
                @media screen and (max-width: 767px) {
                    font-size: 11px;
                }
            }

            svg {
                ${({ fontColor }) => css`
                    ${applyHoverAndPress(fontColor, 'fill')}
                `}
            }
        }
    }
`;

const InputWrapper = styled('div', INPUT_PROPS)`
    position: relative;
    flex-grow: 1;
    padding: 0 5px;
    width: 100%;
    display: flex;
    .control {
        align-self: center;
        width: 100%;
        padding: 1px 0;
    }
    input {
        height: 35px;
        background-color: #ffffff;
        border: 0;
        border-radius: 0px;
        box-shadow: none;
        ${FONT_FAMILY}
        padding: 0;
        font-size: 14px;
        line-height: 36px;
        &:focus,
        &:active {
            box-shadow: none;
        }
        &::placeholder {
            opacity: 0.7;
            font-size: 13px;
            font-weight: normal;
            letter-spacing: normal;
            color: rgba(51, 51, 51, 0.4);
            ${FONT_FAMILY}
        }
    }
    .v-spinner {
        top: 12px;
        right: 5px;
        position: absolute;
    }

    ${(props) =>
        props.hasError &&
        css`
            input {
                border-color: #ef646f;
                &:focus,
                &:active {
                    outline-color: #ef646f;
                    border-color: #ef646f;
                    box-shadow: none;
                    outline-offset: -2px;
                    outline-style: auto;
                    outline-width: 5px;
                }
                @media screen and (max-width: 767px) {
                    height: 35px;
                    .v-spinner {
                        top: 6px;
                        right: 5px;
                    }
                }
            }
            button {
                position: absolute;
                z-index: 1;
                top: 0px;
                height: 100%;
                right: 2px;
                .fas {
                    cursor: pointer;
                    font-size: 20px;
                    color: #ef646f;
                    @media screen and (max-width: 767px) {
                        font-size: 16px;
                    }
                }
            }

            @media screen and (max-width: 767px) {
                .v-spinner {
                    top: 6px;
                    right: 5px;
                }
            }
        `}
`;

export const ArrowIcon = styled.i`
    color: #666666;
    margin: 0 5px;
`;
